import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { toast } from '../../lib/shadcn/sonner'
import {
  useGetDescriptionAiSettings,
  useUpdateDescriptionAiSettings,
} from '../../hooks/backend/settingsPage'
import { HelpText } from '../../components/HelpText'

interface Loaded {
  system_prompt: string
  instruction: string
  default_system_prompt: string
  default_instruction: string
}

interface Draft {
  system_prompt: string
  instruction: string
}

// Settings > AI tools > AI descriptions: the prompts behind "Write with AI"
// in the product dialog.
export default function DescriptionAiSettings() {
  const getSettings = useGetDescriptionAiSettings()
  const updateSettings = useUpdateDescriptionAiSettings()
  const data = getSettings.data as Loaded | null | undefined

  const [draft, setDraft] = useState<Draft | null>(null)

  useEffect(() => {
    void getSettings.trigger({}, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (data) setDraft({ system_prompt: data.system_prompt, instruction: data.instruction })
  }, [data])

  const patch = (p: Partial<Draft>) => setDraft(d => (d ? { ...d, ...p } : d))

  const save = async () => {
    if (!draft) return
    if (!draft.system_prompt.trim() || !draft.instruction.trim()) {
      toast.error('Fill in both prompts')
      return
    }
    try {
      await updateSettings.trigger({
        system_prompt: draft.system_prompt.trim(),
        instruction: draft.instruction.trim(),
      })
      await getSettings.trigger({}, { skipCache: true })
      toast.success('AI description prompts saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save the prompts')
    }
  }

  return (
    <Card id="ai-descriptions">
      <CardHeader>
        <CardTitle>AI descriptions</CardTitle>
        <CardDescription><HelpText>
          In a product's Details tab, Write with AI turns your note about the flavor into a longer
          web description and a short bag description. These are the instructions ChatGPT gets.
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent>
        {getSettings.error && !data ? (
          <p className="text-sm text-destructive">Couldn't load the AI description prompts.</p>
        ) : !draft || !data ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <div className="space-y-4">
            <PromptField
              id="desc-ai-system-prompt"
              label="AI instructions"
              hint="Brand voice and the rules for both descriptions (length, style). It must ask for JSON with web_description and bag_description."
              rows={12}
              value={draft.system_prompt}
              defaultValue={data.default_system_prompt}
              onChange={v => patch({ system_prompt: v })}
            />

            <PromptField
              id="desc-ai-instruction"
              label="Product prompt"
              hint="Sent each time. {label_name} is the Label Name, {notes} is what you typed about the flavor, and {current_description} is the bag description already in the form."
              rows={7}
              value={draft.instruction}
              defaultValue={data.default_instruction}
              onChange={v => patch({ instruction: v })}
            />

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={() => void save()}
                disabled={updateSettings.loading}
                className="gap-1.5"
              >
                {updateSettings.loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function PromptField({
  id,
  label,
  hint,
  rows,
  value,
  defaultValue,
  onChange,
}: {
  id: string
  label: string
  hint: string
  rows: number
  value: string
  defaultValue: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {value.trim() !== defaultValue.trim() && (
          <button
            type="button"
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            onClick={() => onChange(defaultValue)}
          >
            Reset to default
          </button>
        )}
      </div>
      <Textarea id={id} rows={rows} value={value} onChange={e => onChange(e.target.value)} />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  )
}
